import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enable cookies (HttpOnly) for all requests — must be set before any requests fire
axios.defaults.withCredentials = true;

// Protected account emails - must match backend
const MAIN_ADMIN_EMAIL = 'admin@cloudsetu.com';
const SUPPORT_ADMIN_EMAIL = 'support@cloudsetu.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Token kept in-memory only (NOT in localStorage) — persisted via HttpOnly cookie
  const [token, setToken] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState(null);
  const [canDelete, setCanDelete] = useState(false);
  const [isMainAdmin, setIsMainAdmin] = useState(false);
  const [isSupportAdmin, setIsSupportAdmin] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Check if user is admin or support admin (can perform delete operations)
  const checkDeletePermission = (userEmail) => {
    if (!userEmail) return false;
    const email = userEmail.toLowerCase();
    const mainAdmin = email === MAIN_ADMIN_EMAIL.toLowerCase();
    const supportAdmin = email === SUPPORT_ADMIN_EMAIL.toLowerCase();
    setIsMainAdmin(mainAdmin);
    setIsSupportAdmin(supportAdmin);
    setCanDelete(mainAdmin || supportAdmin);
    return mainAdmin || supportAdmin;
  };

  // Fetch user's actual permissions from RBAC endpoint
  const fetchUserPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/my-permissions`);
      const { permissions } = response.data;
      
      // Use the actual permissions array from backend
      // Backend already handles role defaults vs custom permissions
      setUserPermissions(permissions || []);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setUserPermissions([]);
      setPermissionsLoaded(true);
    }
  };

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!permissionsLoaded) return false;
    // Main Admin always has all permissions
    if (isMainAdmin) return true;
    // Check specific permission from user's actual permissions array
    return userPermissions.includes(permission);
  }, [userPermissions, permissionsLoaded, isMainAdmin]);

  // Check if user has any of the given permissions
  const hasAnyPermission = useCallback((permissions) => {
    if (!permissionsLoaded) return false;
    if (isMainAdmin) return true;
    return permissions.some(p => userPermissions.includes(p));
  }, [userPermissions, permissionsLoaded, isMainAdmin]);

  useEffect(() => {
    // Migration: check for legacy localStorage token and set as header for this session
    const legacyToken = localStorage.getItem('token');
    if (legacyToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${legacyToken}`;
      setToken(legacyToken);
    }
    // Always attempt to restore session (cookie or legacy token will authenticate)
    fetchUser().then(() => {
      // After successful session restore, remove legacy localStorage token
      // to complete the migration to HttpOnly cookie auth
      if (legacyToken) {
        localStorage.removeItem('token');
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Check if user is suspended based on is_active field
      const suspended = response.data.is_active === false;
      setIsSuspended(suspended);
      if (suspended) {
        setSuspensionMessage('Your account has been suspended. You can only access the Support system to resolve this issue.');
      } else {
        setSuspensionMessage(null);
      }
      // Check delete permissions
      checkDeletePermission(response.data.email);
      // Fetch RBAC permissions for admin-type users
      const adminRoles = ['admin', 'level_1_support', 'level_2_support', 'level_3_support'];
      if (adminRoles.includes(response.data.role)) {
        await fetchUserPermissions();
      } else {
        setPermissionsLoaded(true);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, portalType = null) => {
    const payload = { email, password };
    if (portalType) payload.portal_type = portalType;
    const response = await axios.post(`${API}/auth/login`, payload);
    const { access_token, user: userData, is_suspended, suspension_message } = response.data;
    // Store token in-memory (for Authorization header within this session)
    setToken(access_token);
    setUser(userData);
    setIsSuspended(is_suspended || false);
    setSuspensionMessage(suspension_message || null);
    checkDeletePermission(userData.email);
    // Set Authorization header (backend also set an HttpOnly cookie automatically)
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    // Remove any legacy localStorage token
    localStorage.removeItem('token');
    
    // Fetch permissions after login for admin-type users
    const adminRoles = ['admin', 'level_1_support', 'level_2_support', 'level_3_support'];
    if (adminRoles.includes(userData.role)) {
      await fetchUserPermissions();
    } else {
      setPermissionsLoaded(true);
    }
    
    return { ...userData, is_suspended, suspension_message };
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    // Store token in-memory only (backend also set HttpOnly cookie)
    setToken(access_token);
    setUser(newUser);
    setIsSuspended(false);
    setSuspensionMessage(null);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    localStorage.removeItem('token');
    setPermissionsLoaded(true);
    return newUser;
  };

  const logout = async () => {
    // Ask backend to clear the HttpOnly cookie
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    // Clear all in-memory state
    setToken(null);
    setUser(null);
    setIsSuspended(false);
    setSuspensionMessage(null);
    setCanDelete(false);
    setIsMainAdmin(false);
    setIsSupportAdmin(false);
    setUserPermissions([]);
    setPermissionsLoaded(false);
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  };

  // Refresh permissions (call this after admin updates permissions)
  const refreshPermissions = async () => {
    await fetchUserPermissions();
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, logout, token, 
      isSuspended, suspensionMessage, 
      canDelete, isMainAdmin, isSupportAdmin,
      userPermissions, permissionsLoaded,
      hasPermission, hasAnyPermission, refreshPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
